'use client';

import React, { Suspense } from 'react';

// Shared Components
import PageHeader from '@/components/hrm/shared/PageHeader';
import StatCard from '@/components/hrm/shared/StatCard';
import StatusBadge from '@/components/hrm/shared/StatusBadge';
import DataTable from '@/components/hrm/shared/DataTable';
import FilterBar from '@/components/hrm/shared/FilterBar';
import TabNav from '@/components/hrm/shared/TabNav';
import ConfirmDialog from '@/components/hrm/shared/ConfirmDialog';
import SlidePanel from '@/components/hrm/shared/SlidePanel';
import Modal from '@/components/hrm/shared/Modal';

export {
  PageHeader,
  StatCard,
  StatusBadge,
  DataTable,
  FilterBar,
  TabNav,
  ConfirmDialog,
  SlidePanel,
  Modal,
};

// Layout
import HRMSidebar from './HRMSidebar';
import HRMHeader from './HRMHeader';
import HRMQuickNav from './HRMQuickNav';
import PunchInFAB from './PunchInFAB';
import HrmUpstreamHealthBanner from '@/components/hrm/HrmUpstreamHealthBanner';
import OrgSetupReadinessBanner from '@/components/hrm/OrgSetupReadinessBanner';
import { Toaster } from 'sonner';
import { usePunchStatus } from '@/hooks/use-punch-status';

// Page loading skeleton
function PageLoading() {
  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div className="space-y-2">
          <div className="hrm-skeleton h-8 w-64" />
          <div className="hrm-skeleton h-4 w-96" />
        </div>
        <div className="hrm-skeleton h-10 w-32 rounded-xl" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="hrm-skeleton h-32 rounded-2xl" />
        ))}
      </div>
      <div className="hrm-skeleton h-96 rounded-2xl" />
    </div>
  );
}

// Shell component that wraps the entire HRM app
export default function HRMShell() {
  return <HRMInner />;

  // We can't use hooks conditionally, so use this inner component
}

import { useState, useCallback, lazy } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import LoginPage from '@/components/hrm/auth/LoginPage';
import { useHrmNavigation } from '@/hooks/use-hrm-navigation';
import { DEFAULT_HRM_PAGE } from '@/lib/hrm-routing';
import { usePermissions } from '@/hooks/use-permissions';
import { getPageInfo } from '@/lib/hrm-navigation';

// Lazy loaded page map - built dynamically from navigation config
const pageModules: Record<string, React.LazyExoticComponent<React.ComponentType>> = {};

// We'll populate this at module load time
function loadPageComponents() {
  const pages: Record<string, () => Promise<{ default: React.ComponentType }>> = {
    // Dashboard & People
    'dashboard': () => import('@/components/hrm/pages/dashboard/DashboardPage'),
    'dashboard-hr': () => import('@/components/hrm/pages/dashboard/dashboardHR'),
    'employee-directory': () => import('@/components/hrm/pages/people/employee-directory'),
    'self-onboard-invites': () => import('@/components/hrm/pages/people/SelfOnboardInvitesPage'),
    'employee-self-service': () => import('@/components/hrm/pages/people/employee-self-service'),
    'employee-profile': () => import('@/components/hrm/pages/people/EmployeeProfilePage'),
    'my-team': () => import('@/components/hrm/pages/people/my-team'),
    // Organization
    'org-structure': () => import('@/components/hrm/pages/organization/OrgStructurePage'),
    'legal-entities': () => import('@/components/hrm/pages/organization/LegalEntitiesPage'),
    'business-units': () => import('@/components/hrm/pages/organization/BusinessUnitsPage'),
    'departments': () => import('@/components/hrm/pages/organization/DepartmentsPage'),
    'designations': () => import('@/components/hrm/pages/organization/DesignationsPage'),
    'locations': () => import('@/components/hrm/pages/organization/LocationsPage'),
    'cost-centers': () => import('@/components/hrm/pages/organization/CostCentersPage'),
    'pay-grades': () => import('@/components/hrm/pages/organization/PayGradesPage'),
    'pay-bands': () => import('@/components/hrm/pages/organization/PayBandsPage'),
    // Time & Attendance
    'shifts': () => import('@/components/hrm/pages/time-attendance/ShiftsPage'),
    'shift-roster': () => import('@/components/hrm/pages/time-attendance/ShiftRosterHome'),
    'shift-swaps': () => import('@/components/hrm/pages/time-attendance/ShiftSwapsPage'),
    'attendance': () => import('@/components/hrm/pages/time-attendance/AttendancePage'),
    'punch-clock': () => import('@/components/hrm/pages/time-attendance/PunchClockPage'),
    'geofence': () => import('@/components/hrm/pages/time-attendance/GeofencePage'),
    'field-tracker': () => import('@/components/hrm/pages/FieldTracker'),
    'attendance-summary': () => import('@/components/hrm/pages/time-attendance/AttendanceSummaryPage'),
    'overtime-management': () => import('@/components/hrm/pages/time-attendance/OvertimePage'),
    'regularization': () => import('@/components/hrm/pages/time-attendance/RegularizationPage'),
    'wfh-settings': () => import('@/components/hrm/pages/time-attendance/WfhSettingsPage'),
    'wfh-requests': () => import('@/components/hrm/pages/time-attendance/WfhRequestsPage'),
    // Leave
    'leave-types': () => import('@/components/hrm/pages/leave/LeaveTypesPage'),
    'leave-settings': () => import('@/components/hrm/pages/leave/LeaveSettingsPage'),
    'leave-balance': () => import('@/components/hrm/pages/leave/LeaveBalancePage'),
    'leave-applications': () => import('@/components/hrm/pages/leave/LeaveApplicationsPage'),
    'leave-approvals': () => import('@/components/hrm/pages/leave/LeaveApprovalsPage'),
    'leave-team-calendar': () => import('@/components/hrm/pages/leave/LeaveTeamCalendarPage'),
    'leave-encashment': () => import('@/components/hrm/pages/leave/LeaveEncashmentPage'),
    'holiday-calendar': () => import('@/components/hrm/pages/leave/HolidayCalendarPage'),
    // Payroll (consolidated)
    'payroll-dashboard': () => import('@/components/hrm/pages/payroll/PayrollDashboardPage'),
    'salary-components': () => import('@/components/hrm/pages/payroll/SalaryComponentsPage'),
    'salary-structures': () => import('@/components/hrm/pages/payroll/SalaryStructuresPage'),
    'payroll-run': () => import('@/components/hrm/pages/payroll/PayrollRunPage'),
    'payslips': () => import('@/components/hrm/pages/payroll/PayslipsPage'),
    'loan-management': () => import('@/components/hrm/pages/payroll/LoanManagementPage'),
    // Recruitment (consolidated)
    'job-postings': () => import('@/components/hrm/pages/recruitment/JobPostingsPage'),
    'requisitions': () => import('@/components/hrm/pages/recruitment/RequisitionsPage'),
    'candidate-pipeline': () => import('@/components/hrm/pages/recruitment/CandidatePipelinePage'),
    'resume-parser': () => import('@/components/hrm/pages/recruitment/ResumeParserPage'),
    'employee-referrals': () => import('@/components/hrm/pages/recruitment/EmployeeReferralsPage'),
    'interview-schedule': () => import('@/components/hrm/pages/recruitment/InterviewSchedulePage'),
    'offer-management': () => import('@/components/hrm/pages/recruitment/OfferManagementPage'),
    'career-fair': () => import('@/components/hrm/pages/recruitment/CareerFairPage'),
    'hiring-analytics': () => import('@/components/hrm/pages/recruitment/HiringAnalyticsPage'),
    'internal-mobility': () => import('@/components/hrm/pages/recruitment/InternalMobilityPage'),
    // Performance (consolidated)
    'performance-cycles': () => import('@/components/hrm/pages/performance/PerformanceCyclesPage'),
    'goals': () => import('@/components/hrm/pages/performance/GoalsPage'),
    'feedback': () => import('@/components/hrm/pages/performance/FeedbackPage'),
    'nine-box': () => import('@/components/hrm/pages/performance/NineBoxPage'),
    'calibration': () => import('@/components/hrm/pages/performance/CalibrationPage'),
    'competency-framework': () => import('@/components/hrm/pages/performance/CompetencyPage'),
    'pip-tracker': () => import('@/components/hrm/pages/performance/PIPTrackerPage'),
    'team-performance': () => import('@/components/hrm/pages/performance/TeamPerformancePage'),
    // Training
    'training': () => import('@/components/hrm/pages/training/TrainingPage'),
    'training-calendar': () => import('@/components/hrm/pages/training/TrainingCalendarPage'),
    'training-materials': () => import('@/components/hrm/pages/training/TrainingMaterialsPage'),
    'training-certificates': () => import('@/components/hrm/pages/training/TrainingCertificatesPage'),
    'training-feedback': () => import('@/components/hrm/pages/training/TrainingFeedbackPage'),
    'learning-paths': () => import('@/components/hrm/pages/training/LearningPathsPage'),
    'mentoring-program': () => import('@/components/hrm/pages/training/MentoringPage'),
    // Management (consolidated)
    'expenses': () => import('@/components/hrm/pages/management/ExpensesPage'),
    'travel-management': () => import('@/components/hrm/pages/management/TravelPage'),
    'tickets': () => import('@/components/hrm/pages/management/TicketsPage'),
    // Projects (consolidated)
    'projects': () => import('@/components/hrm/pages/projects/ProjectsPage'),
    'timesheets': () => import('@/components/hrm/pages/projects/TimesheetsPage'),
    // Surveys (consolidated)
    'surveys': () => import('@/components/hrm/pages/surveys/SurveysPage'),
    'survey-analytics': () => import('@/components/hrm/pages/surveys/SurveyAnalyticsPage'),
    'pulse-survey': () => import('@/components/hrm/pages/surveys/PulseSurveyPage'),
    // Assets (consolidated)
    'assets': () => import('@/components/hrm/pages/assets/AssetsPage'),
    'asset-categories': () => import('@/components/hrm/pages/assets/AssetRegisterPages').then((m) => ({ default: m.AssetCategoriesPage })),
    'asset-requests': () => import('@/components/hrm/pages/assets/AssetWorkspacePages').then((m) => ({ default: m.AssetRequestsPage })),
    'asset-vendors': () => import('@/components/hrm/pages/assets/AssetWorkspacePages').then((m) => ({ default: m.AssetVendorsPage })),
    'asset-purchases': () => import('@/components/hrm/pages/assets/AssetWorkspacePages').then((m) => ({ default: m.AssetPurchasesPage })),
    'asset-maintenance': () => import('@/components/hrm/pages/assets/AssetWorkspacePages').then((m) => ({ default: m.AssetMaintenancePage })),
    'asset-depreciation': () => import('@/components/hrm/pages/assets/AssetWorkspacePages').then((m) => ({ default: m.AssetDepreciationPage })),
    'asset-licenses': () => import('@/components/hrm/pages/assets/AssetRegisterPages').then((m) => ({ default: m.AssetLicensesPage })),
    'asset-stock': () => import('@/components/hrm/pages/assets/AssetRegisterPages').then((m) => ({ default: m.AssetStockPage })),
    'asset-cover': () => import('@/components/hrm/pages/assets/AssetRegisterPages').then((m) => ({ default: m.AssetCoverPage })),
    'asset-incidents': () => import('@/components/hrm/pages/assets/AssetRegisterPages').then((m) => ({ default: m.AssetIncidentsPage })),
    'asset-labels': () => import('@/components/hrm/pages/assets/AssetWorkspacePages').then((m) => ({ default: m.AssetLabelsPage })),
    'asset-controls': () => import('@/components/hrm/pages/assets/AssetWorkspacePages').then((m) => ({ default: m.AssetControlsPage })),
    // Lifecycle (consolidated)
    'onboarding': () => import('@/components/hrm/pages/lifecycle/OnboardingPage'),
    'onboarding-guide': () => import('@/components/hrm/pages/help/OnboardingUserGuidePage'),
    'offboarding': () => import('@/components/hrm/pages/lifecycle/OffboardingPage'),
    // Engagement
    'policies': () => import('@/components/hrm/pages/engagement/PoliciesPage'),
    'benefits': () => import('@/components/hrm/pages/engagement/BenefitsPage'),
    'rewards': () => import('@/components/hrm/pages/engagement/RewardsPage'),
    'documents': () => import('@/components/hrm/pages/engagement/DocumentsPage'),
    'announcements': () => import('@/components/hrm/pages/engagement/AnnouncementsPage'),
    'notifications': () => import('@/components/hrm/pages/engagement/NotificationsPage'),
    'company-calendar': () => import('@/components/hrm/pages/engagement/CompanyCalendarPage'),
    'grievance-management': () => import('@/components/hrm/pages/engagement/GrievancesPage'),
    'recognition-wall': () => import('@/components/hrm/pages/engagement/RecognitionWallPage'),
    'meeting-scheduler': () => import('@/components/hrm/pages/engagement/MeetingSchedulerPage'),
    'employee-engagement': () => import('@/components/hrm/pages/engagement/EngagementScorePage'),
    // Analytics (consolidated)
    'reports': () => import('@/components/hrm/pages/analytics/ReportsPage'),
    'workforce-planning': () => import('@/components/hrm/pages/analytics/WorkforcePlanningPage'),
    'attrition-risk': () => import('@/components/hrm/pages/analytics/AttritionRiskPage'),
    'org-chart': () => import('@/components/hrm/pages/analytics/OrgChartPage'),
    'separation-analysis': () => import('@/components/hrm/pages/analytics/SeparationAnalysisPage'),
    // Compliance
    'compliance-dashboard': () => import('@/components/hrm/pages/compliance/CompliancePage'),
    'contingent-workers': () => import('@/components/hrm/pages/compliance/ContractorsPage'),
    'visa-immigration': () => import('@/components/hrm/pages/compliance/VisaPage'),
    'workplace-safety': () => import('@/components/hrm/pages/compliance/WorkplaceSafetyPage'),
    'risk-register': () => import('@/components/hrm/pages/compliance/RiskRegisterPage'),
    // System (consolidated)
    'setup': () => import('@/components/hrm/pages/system/OrgSetupPage'),
    'settings': () => import('@/components/hrm/pages/system/SettingsPage'),
    'picklists': () => import('@/components/hrm/pages/system/PicklistsPage'),
    'dashboard-layout': () => import('@/components/hrm/pages/system/DashboardLayoutPage'),
    'roles-permissions': () => import('@/components/hrm/pages/system/RolesPermissionsPage'),
    'audit-log': () => import('@/components/hrm/pages/system/AuditLogPage'),
    'workflow-builder': () => import('@/components/hrm/pages/system/WorkflowBuilderPage'),
    'email-templates': () => import('@/components/hrm/pages/system/EmailTemplatesPage'),
    'webhooks': () => import('@/components/hrm/pages/system/WebhooksPage'),
    'ai-chatbot': () => import('@/components/hrm/pages/system/AIChatbotPage'),
    'help-center': () => import('@/components/hrm/pages/system/HelpCenterPage'),
    'data-import-export': () => import('@/components/hrm/pages/system/DataImportExportPage'),
    'quick-actions': () => import('@/components/hrm/pages/system/QuickActionsPage'),
    'milestone-tracker': () => import('@/components/hrm/pages/system/MilestoneTrackerPage'),
  };

  // Create lazy components
  Object.entries(pages).forEach(([key, loader]) => {
    pageModules[key] = lazy(loader);
  });
}

// Initialize all lazy page components
loadPageComponents();

const SIDEBAR_W_EXPANDED = 260;
const SIDEBAR_W_COLLAPSED = 72;

function HRMInner() {
  const { isAuthenticated } = useAuthStore();
  const { currentPage, goTo: handleNavigate } = useHrmNavigation();
  const { canView, isRolePreview } = usePermissions();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isPunchedIn, canPunchIn, canPunchOut } = usePunchStatus();

  React.useEffect(() => {
    try {
      if (localStorage.getItem('hrm-sidebar-collapsed') === '1') {
        setSidebarCollapsed(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const handleToggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try {
        localStorage.setItem('hrm-sidebar-collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Get the lazy page component (may be undefined for unknown URL keys at runtime)
  const PageComponent = pageModules[currentPage] as
    | React.LazyExoticComponent<React.ComponentType>
    | undefined;
  // Permission guard: a known page the user is not allowed to open is denied,
  // even via a direct URL. `employee-profile` is always self-accessible.
  const isKnownPage = Boolean(PageComponent);
  const isAllowed =
    !isKnownPage || currentPage === 'employee-profile' || canView(currentPage);

  return (
    <div className="min-h-screen bg-[var(--hrm-body-bg)] dark:bg-slate-950">
      {/* Sidebar */}
      <HRMSidebar
        currentPage={currentPage}
        onNavigate={handleNavigate}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        expandedWidth={SIDEBAR_W_EXPANDED}
        collapsedWidth={SIDEBAR_W_COLLAPSED}
      />

      {/* Header */}
      <HRMHeader
        onToggleSidebar={handleToggleSidebar}
        onToggleDesktopCollapse={handleToggleSidebarCollapse}
        isSidebarCollapsed={sidebarCollapsed}
        onNavigate={handleNavigate}
        currentPage={currentPage}
      />

      {/* Right: fixed personal icon shortcuts */}
      <HRMQuickNav
        currentPage={currentPage}
        onNavigate={handleNavigate}
      />

      {/* Main Content */}
      <main
        className={`min-h-screen transition-[margin,padding] duration-300 ease-in-out mr-[72px] ${
          isRolePreview ? 'pt-[6.25rem]' : 'pt-16'
        } ${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'}`}
      >
        <HrmUpstreamHealthBanner />
        <OrgSetupReadinessBanner />
        <Suspense fallback={<PageLoading />}>
          {PageComponent && isAllowed ? (
            <PageComponent />
          ) : (
            <div className="p-4 lg:p-6 flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <i
                  className={`${
                    isKnownPage && !isAllowed ? 'ri-lock-2-line' : 'ri-error-warning-line'
                  } text-5xl text-slate-300 dark:text-slate-600 mb-4 block`}
                />
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                  {isKnownPage && !isAllowed ? 'Access Restricted' : 'Page Not Found'}
                </h2>
                <p className="text-sm text-slate-400">
                  {isKnownPage && !isAllowed
                    ? `You don't have permission to view ${
                        getPageInfo(currentPage)?.title ?? 'this page'
                      }. Contact your administrator if you need access.`
                    : `The page "${currentPage}" does not exist or is not available for your role.`}
                </p>
                <button
                  onClick={() => handleNavigate(DEFAULT_HRM_PAGE)}
                  className="mt-4 px-4 py-2 bg-[var(--hrm-primary)] hover:bg-[var(--hrm-primary-dark)] text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          )}
        </Suspense>
      </main>

      {/* Floating Action Button */}
      <PunchInFAB
        isPunchedIn={isPunchedIn}
        canPunch={canPunchIn || canPunchOut}
        onPunchIn={() => handleNavigate('punch-clock')}
      />

      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
